import { UseMutationOptions, UseQueryOptions, useMutation, useQuery } from '@tanstack/react-query';
import { EXPORT_JOBS_CSV_QUERY_KEY, POST_KEY } from '../queryKeys';
import { queryYemboApiCall, queryYemboApiCallWithReturnValue } from '../queryYemboApiCalls';

const URL = 'export-request';

type ExportJobsCSVStatus = 'not_started' | 'pending' | 'failed' | 'completed';

type ExportJobsCSVData = {
  location: string;
  key: string;
  requesterKey: string | null;
  requestedAt: string | null;
  companyKeys: string[] | null;
  createdAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  downloadedAt: string | null;
  expiresAt: string | null;
  totalRows: number;
  processedRows: number;
  currentBatch: number;
  filters: Record<string, string>;
  status: ExportJobsCSVStatus;
  warnings: string[];
  url: string | null;
};

type ExportJobsCSVResponse = {
  exportRequest: ExportJobsCSVData;
  isConcurrencyError?: boolean | null;
};

export class ExportJobsCSVError extends Error {
  constructor(
    message: string,
    readonly isConcurrencyError: boolean
  ) {
    super(message);
  }
}

type ExportJobsCSVParams = {
  filterParams?: URLSearchParams;
  expectedRows: number;
};

const exportJobsCSV = async ({ filterParams, expectedRows }: ExportJobsCSVParams) => {
  const response = await queryYemboApiCall({
    url: URL,
    method: 'POST',
    urlParams: filterParams,
    body: JSON.stringify({ expectedRows }),
  });

  const body = (await response.json()) as ExportJobsCSVResponse;

  if (!response.ok)
    throw new ExportJobsCSVError('Failed to initiate jobs CSV export', body.isConcurrencyError ?? false);

  return body;
};

export const useExportJobsCSV = (
  options?: UseMutationOptions<ExportJobsCSVResponse, ExportJobsCSVError, ExportJobsCSVParams>
) => {
  return useMutation({
    ...options,
    mutationKey: [EXPORT_JOBS_CSV_QUERY_KEY, POST_KEY],
    mutationFn: exportJobsCSV,
  });
};

type GetExportJobsDataResponse = ExportJobsCSVData & {
  hasWarnings: boolean;
  hasSizeWarnings: boolean;
  latestWarning: string;
  isTerminal: boolean;
  isInProgress: boolean;
  progressPercentage: number;
};

const getExportJobsData = async (key: string) => {
  const exportRequest = await queryYemboApiCallWithReturnValue<GetExportJobsDataResponse, 'exportRequest'>({
    url: URL,
    urlParams: new URLSearchParams({ key }),
    key: 'exportRequest',
  });

  if (!exportRequest) throw new Error('Export request not found');

  return exportRequest;
};

export const useGetExportJobsData = (key: string, options?: Partial<UseQueryOptions<GetExportJobsDataResponse>>) => {
  return useQuery({
    ...options,
    queryKey: [EXPORT_JOBS_CSV_QUERY_KEY, key],
    queryFn: () => getExportJobsData(key),
  });
};
